import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { Checkbox } from '@components/moleculs/checkbox';
import { ClickAwayListener } from '@components/moleculs/click-away-listener';
import { Popper } from '@components/moleculs/popper';
import { Modal } from '@components/organisms/welcome-modal/styles';
import { useToast } from '@hooks/use-toast';
import { DocsVerificationTabEnum } from '@models/enums';
import { CDN_URL } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/demo-data-button/constants';
import { useDemoData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { Fade } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useAppSelector } from '@store';

export const DemoDataButton = () => {
    const { uploadFileRef } = useDemoData();
    const { showToast } = useToast();
    const anchorEl = useRef<HTMLButtonElement | null>(null);
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [previewSrc, setPreviewSrc] = useState('');
    const [selectedSrc, setSelectedSrc] = useState('');
    const currentTab = useAppSelector((state) => state.docsVerification.currentTab);
    const [isLoading, setIsLoading] = useState(false);

    const poiDocuments = useMemo(
        () => Array.from({ length: 6 }, (_, i) => `${CDN_URL}/widget-demo/deepfake/poi/${i}.png`),
        [],
    );

    const livenessDocuments = useMemo(
        () =>
            Array.from({ length: 5 }, (_, i) => ({
                image: `${CDN_URL}/widget-demo/deepfake/liveness/${i + 1}.png`,
                video: `${CDN_URL}/widget-demo/deepfake/liveness/${i + 1}.mp4`,
            })),
        [],
    );

    const handleClose = () => {
        if (previewSrc) {
            return;
        }
        setIsOpen(false);
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleSelect = async () => {
        if (isLoading || !selectedSrc) {
            return;
        }
        try {
            const blob = await fetch(selectedSrc).then((r) => r.blob());

            const dataTransfer = new DataTransfer();

            const name = selectedSrc.split('/').pop();

            dataTransfer.items.add(
                new File([blob], name || 'image.png', {
                    type: 'image/png',
                    lastModified: new Date().getTime(),
                }),
            );

            uploadFileRef?.current?.({
                target: {
                    files: dataTransfer.files,
                },
            } as React.ChangeEvent<HTMLInputElement>);
            handleClose();
        } catch (e) {
            const message =
                e?.data?.message || t('docVerificationWidget.verificationError.subTitle"');

            showToast({
                variant: 'error',
                title: t('common.somethingWentWrong'),
                message,
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (
        currentTab !== DocsVerificationTabEnum.DOCUMENT &&
        currentTab !== DocsVerificationTabEnum.BIOMETRICS
    ) {
        return null;
    }

    return (
        <React.Fragment>
            <Popper
                open={isOpen}
                anchorEl={anchorEl.current}
                placement='top-end'
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                ]}
                sx={{ zIndex: 1200 }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={200}>
                        <ClickAwayListener
                            onClickAway={handleClose}
                            mouseEvent={previewSrc ? false : 'onClick'}
                            touchEvent={previewSrc ? false : 'onTouchStart'}
                        >
                            <Stack
                                sx={{
                                    borderRadius: '16px',
                                    borderColor: 'primaryA3',
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    padding: 2,
                                    minWidth: '320px',
                                    overflow: 'auto',
                                    backgroundColor: 'common.white',
                                    mr: {
                                        xs: 2,
                                        sm: 2,
                                        lg: 0,
                                        xl: 0,
                                    },
                                    ml: {
                                        xs: 2,
                                        sm: 2,
                                        lg: 0,
                                        xl: 0,
                                    },
                                }}
                            >
                                <Stack
                                    sx={{ gap: 2, justifyContent: 'center', alignItems: 'center' }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {currentTab === DocsVerificationTabEnum.DOCUMENT && (
                                            <React.Fragment>
                                                {poiDocuments.map((item) => (
                                                    <Stack
                                                        sx={{
                                                            gap: 1,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            component='img'
                                                            src={item}
                                                            sx={{
                                                                minHeight: '140px',
                                                                maxHeight: '140px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s',
                                                                borderRadius: '4px',
                                                                '&:hover': {
                                                                    opacity: 0.5,
                                                                },
                                                            }}
                                                            onClick={() => {
                                                                setPreviewSrc(item);
                                                            }}
                                                        />
                                                        <Checkbox
                                                            checked={selectedSrc === item}
                                                            onClick={() => {
                                                                setSelectedSrc((prevState) => {
                                                                    if (prevState === item) {
                                                                        return '';
                                                                    }

                                                                    return item;
                                                                });
                                                            }}
                                                        />
                                                    </Stack>
                                                ))}
                                            </React.Fragment>
                                        )}
                                        {currentTab === DocsVerificationTabEnum.BIOMETRICS && (
                                            <React.Fragment>
                                                {livenessDocuments.map((item) => (
                                                    <Stack
                                                        sx={{
                                                            gap: 1,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            component='img'
                                                            src={item.image}
                                                            sx={{
                                                                minHeight: '140px',
                                                                maxHeight: '140px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s',
                                                                borderRadius: '4px',
                                                                '&:hover': {
                                                                    opacity: 0.5,
                                                                },
                                                            }}
                                                            onClick={() => {
                                                                setPreviewSrc(item.video);
                                                            }}
                                                        />
                                                        <Checkbox
                                                            checked={selectedSrc === item.image}
                                                            onClick={() => {
                                                                setSelectedSrc((prevState) => {
                                                                    if (prevState === item.image) {
                                                                        return '';
                                                                    }

                                                                    return item.image;
                                                                });
                                                            }}
                                                        />
                                                    </Stack>
                                                ))}
                                            </React.Fragment>
                                        )}
                                    </Box>
                                    <Button
                                        variant='tertiary'
                                        size='m'
                                        disabled={!selectedSrc}
                                        onClick={handleSelect}
                                        loading={isLoading}
                                    >
                                        {t('common.select')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </ClickAwayListener>
                    </Fade>
                )}
            </Popper>
            <Button
                ref={anchorEl}
                variant='primary'
                size='xl'
                sx={{
                    position: 'fixed',
                    right: '16px',
                    bottom: '16px',
                }}
                onClick={handleOpen}
            >
                {t('demoData.button')}
            </Button>
            <Modal
                open={!!previewSrc}
                width='100%'
                hasCloser={true}
                onClose={() => {
                    setPreviewSrc('');
                }}
            >
                <Box
                    component={previewSrc.includes('mp4') ? 'video' : 'img'}
                    sx={{
                        mt: {
                            xs: 6,
                            sm: 6,
                            md: 4,
                            lg: 2,
                            xl: 2,
                        },
                    }}
                    src={previewSrc}
                    controls={true}
                />
            </Modal>
        </React.Fragment>
    );
};
